import { create } from 'zustand'
import { ToastContainer, ToastProps } from './toast'

interface ToastState {
  toasts: ToastProps[]
  addToast: (toast: Omit<ToastProps, 'id' | 'onClose'>) => void
  removeToast: (id: string) => void
}

const useToastStore = create<ToastState>()((set) => ({
  toasts: [],
  addToast: (toast) => {
    const id = Math.random().toString(36).substring(2, 9)
    const newToast: ToastProps = {
      ...toast,
      id,
      onClose: (toastId) => useToastStore.getState().removeToast(toastId)
    }
    
    set((state) => ({
      toasts: [...state.toasts, newToast]
    }))
  },
  removeToast: (id) => {
    set((state) => ({
      toasts: state.toasts.filter((toast) => toast.id !== id)
    }))
  }
}))

/**
 * Renders the toasts pushed through `useToast`. Mount this once, near the app
 * root; without it every `toast(...)` call is silently swallowed.
 */
export function ToastViewport() {
  const toasts = useToastStore((state) => state.toasts)
  const removeToast = useToastStore((state) => state.removeToast)

  return <ToastContainer toasts={toasts} onClose={removeToast} />
}

export function useToast() {
  const { addToast } = useToastStore()

  type LegacyToastOptions = {
    title: string
    description?: string
    variant?: 'destructive' | 'default'
  }

  const toast = (options: Omit<ToastProps, 'id' | 'onClose'> | LegacyToastOptions) => {
    // Handle legacy format
    if ('variant' in options && options.variant) {
      const type = options.variant === 'destructive' ? 'error' : 'success'
      addToast({ 
        type, 
        title: options.title, 
        message: options.description 
      })
    } else if ('type' in options) {
      addToast(options as Omit<ToastProps, 'id' | 'onClose'>)
    } else {
      // Default to info if no type specified
      addToast({ 
        type: 'info', 
        title: options.title, 
        message: options.description 
      })
    }
  }
  
  toast.success = (title: string, message?: string) => {
    addToast({ type: 'success', title, message })
  }
  
  toast.error = (title: string, message?: string) => {
    addToast({ type: 'error', title, message })
  }
  
  toast.info = (title: string, message?: string) => {
    addToast({ type: 'info', title, message })
  }
  
  toast.warning = (title: string, message?: string) => {
    addToast({ type: 'warning', title, message })
  }
  
  return { toast }
}

export { useToastStore }
